import Element, { ElementType } from '../element';
import { IShader } from '../shaders/shader';
import { Texture, PBRMetallicRoughnessMaterial, Material, BaseTexture, PositionNormalTextureVertex } from '@babylonjs/core';
import store from '@/store';
import { BaseCommand } from './baseCommand';
import { ChangeRafixColorCommand } from './changeRafixColorCommand';
import { wallManager } from '../wallManager';
import { MaterialData } from '../shaders/materialData';

export default class ChangeColorCommand extends BaseCommand {
    protected mutation!: string;
    protected previousShader!: IShader;
    protected previousMaterialData: Map<Element, MaterialData> = new Map();

    constructor(
        scenePreview: any,
        protected elements: Element[],
        protected shader: IShader,
        private onStart: () => void = () => store.commit('incrementTasks'),
        private onFinish: () => void = () => store.commit('decrementTasks'),
        private onlySelected: boolean = false,
    ) {
        super(scenePreview);
        this.savePreviousColors();
    }

    public execute(): void {
        this.onStart();
        this.manageShaders();
        this.applyMaterial();
    }

    public revert(): void {
        this.revertCommands();
        this.previousMaterialData.forEach((data, element) => {
            this.applyMaterialData(element, data);
            element.shaderName = this.previousShader.key;
        });
        if (!this.onlySelected) {
            store.commit(this.mutation, this.previousShader);
        }
    }

    protected applyMaterial(name: string = '') {
        const material = this.shader.getMaterial(this.scenePreview.scene, '');
        if (material instanceof PBRMetallicRoughnessMaterial) {
            let baseTextureLoaded = false;
            let normalTextureLoaded = false;
            const onLoaded = () => {
                if (baseTextureLoaded && normalTextureLoaded) {
                    this.elements.forEach((element) => {
                        const textures = this.getTextures(material);
                        const data = new MaterialData(
                            material.metallic,
                            material.roughness,
                            textures[0],
                            textures[1],
                        );
                        this.applyMaterialData(element, data, name);
                        element.shaderName = this.shader.key;
                    });
                    this.onFinish();
                }
            };

            (material.baseTexture as Texture).onLoadObservable.add(() => {
                baseTextureLoaded = true;
                onLoaded();
            });
            (material.normalTexture as Texture).onLoadObservable.add(() => {
                normalTextureLoaded = true;
                onLoaded();
            });
        }
    }

    protected applyMaterialData(
        element: Element,
        data: MaterialData,
        name: string = '') {
        let meshes = element.meshes;
        if (name !== '') {
            meshes = element.meshes.filter((x) => x.name.includes(name));
        }
        meshes.forEach((mesh) => {
            if (mesh.material instanceof PBRMetallicRoughnessMaterial &&
                !mesh.name.includes('hdf') &&
                !mesh.name.includes('rafix') &&
                !mesh.name.includes('edge')) {
                mesh.material.baseTexture = data.baseTexture;
                mesh.material.normalTexture = data.normalTexture;
                mesh.material.metallic = data.metalic;
                mesh.material.roughness = data.roughness;
            }
        });
    }

    protected savePreviousColors() {
        this.elements.forEach((element) => {
            const material = element.meshes[0].material!;
            if (!(material instanceof PBRMetallicRoughnessMaterial)) {
                return;
            }
            const textures = this.getTextures(material);
            const data = new MaterialData(
                material.metallic,
                material.roughness,
                textures[0],
                textures[1],
            );
            this.previousMaterialData.set(element, data);
        });
    }

    protected getTextures(material: Material): [BaseTexture, BaseTexture] {
        const m = material as PBRMetallicRoughnessMaterial;
        const baseTexture = m.baseTexture;
        const normalTexture = m.normalTexture;
        return [baseTexture, normalTexture];
    }

    private manageShaders() {
        if (this.elements.length) {
            const shaders = store.getters.shaders;
            const type = this.elements[0].type;
            switch (type) {
                case ElementType.BODY: {
                    this.previousShader = shaders.selectedBody;
                    this.mutation = 'selectBody';
                    const currestSupportShader = store.getters.shaders.selectedSupport;
                    if (!wallManager.canPlaceWallSupports()) {
                        store.commit('selectSupportByShader', this.shader);
                        this.handleSupportsColor(currestSupportShader);
                    }
                    break;
                }
                case ElementType.INSIDE: {
                    this.previousShader = shaders.selectedShelf;
                    this.mutation = 'selectShelf';
                    break;
                }
                case ElementType.WALL: {
                    this.previousShader = shaders.selectedWall;
                    this.mutation = 'selectWall';
                    const currestSupportShader = store.getters.shaders.selectedSupport;
                    const currentRafixShader = store.getters.shaders.selectedRafix;
                    if (wallManager.canPlaceWallSupports()) {
                        store.commit('selectSupportByShader', this.shader);
                        this.handleSupportsColor(currestSupportShader);
                    }
                    store.commit('selectRafixByShader', this.shader);
                    this.handleRafixesColor(currentRafixShader);
                    break;
                }
                case ElementType.FRONT: {
                    this.previousShader = shaders.selectedFront;
                    this.mutation = 'selectFront';
                    break;
                }
                case ElementType.WALL_SUPPORT: {
                    this.previousShader = shaders.selectedSupport;
                    this.mutation = 'selectSupport';
                    break;
                }
                case ElementType.BEDHEAD: {
                    this.previousShader = shaders.selectedWall;
                    this.mutation = 'selectWall';
                    break;
                }
            }
            if (!this.onlySelected) {
                store.commit(this.mutation, this.shader);
            }
        }
    }

    private handleSupportsColor(currentSupportShader: IShader) {
        if (currentSupportShader !== store.getters.shaders.selectedSupport) {
            const supports = this.scenePreview.elements
                .filter((x: Element) => x.type === ElementType.WALL_SUPPORT);
            if (!supports.length) { return; }
            const command = new ChangeColorCommand(
                this.scenePreview,
                supports,
                store.getters.shaders.selectedSupport,
                this.onStart,
                this.onFinish,
            );
            this.addCommand(command);
        }
    }

    private handleRafixesColor(currentRafixShader: IShader) {
        if (currentRafixShader !== store.getters.shaders.selectedRafix) {
            const rafixElements = this.elements.filter((element) =>
                element.meshes.some((mesh) => mesh.name.includes('rafix')),
            );
            const command = new ChangeRafixColorCommand(
                this.scenePreview,
                rafixElements,
                store.getters.shaders.selectedRafix,
                this.onStart,
                this.onFinish,
            );
            this.addCommand(command);
        }
    }
}
